"use client";

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import {
  Plus,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { CategoryService, type CategoryTreeNode } from "@/services/category.service";
import { CatalogueTemplateService } from "@/services/catalogue-template.service";
import { ApplyConfigurationModal } from "../apply-configuration-modal";
import {
  ancestorsOf,
  categoryStatuses,
  descendantCount,
  descendantIds,
} from "@/lib/categories/tree-model";
import { CategoryFormDialog, type CategoryFormValues } from "./category-form-dialog";
import { CategoryNavigator } from "./category-navigator";
import { ConfirmDialog } from "./confirm-dialog";
import { SchemaEditor } from "./schema-editor";
import { createDraftStore } from "./use-category-drafts";
import { CategoryRowMenu } from "./category-row-menu";
import type { LanguageCode } from "@/constants/languages";
import { localizedText } from "@/lib/categories/localization";

const GENERAL_ID = "general";

interface CategoryManagementProps {
  catalogueId: string;
  /** When set, the catalogue is soft-deleted → render read-only. */
  deletedAt?: string;
  languages: LanguageCode[];
}

function findNode(nodes: CategoryTreeNode[], id: string): CategoryTreeNode | null {
  for (const n of nodes) {
    if (n.id === id) return n;
    const found = findNode(n.children, id);
    if (found) return found;
  }
  return null;
}

export function CategoryManagement({
  catalogueId,
  deletedAt,
  languages,
}: CategoryManagementProps) {
  const readOnly = !!deletedAt;

  const [tree, setTree] = useState<CategoryTreeNode[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [selectedId, setSelectedId] = useState<string | null>(GENERAL_ID);
  const [editorReload, setEditorReload] = useState(0);
  const draftStore = useRef(createDraftStore()).current;
  const [draftIds, setDraftIds] = useState<ReadonlySet<string>>(new Set());

  const [applyConfigurationOpen, setApplyConfigurationOpen] = useState(false);
  const [queueNotice, setQueueNotice] = useState<string | null>(null);

  // Create / rename form.
  const [form, setForm] = useState<
    | { mode: "create"; parent: CategoryTreeNode | null }
    | { mode: "rename"; target: CategoryTreeNode }
    | null
  >(null);
  const [formBusy, setFormBusy] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);

  // Destructive confirmations, replacing window.confirm.
  const [confirm, setConfirm] = useState<
    | { kind: "delete"; node: CategoryTreeNode }
    | { kind: "disable"; node: CategoryTreeNode }
    | null
  >(null);
  const [confirmBusy, setConfirmBusy] = useState(false);

  // Failures used to be alert(); they now sit beside the success notice.
  const [actionError, setActionError] = useState<string | null>(null);

  const selectedNode =
    selectedId && selectedId !== GENERAL_ID ? findNode(tree, selectedId) : null;
  const statuses = useMemo(() => categoryStatuses(tree), [tree]);
  const selectedStatus = selectedNode ? statuses.get(selectedNode.id) : undefined;

  const loadTree = useCallback(
    async () => {
      setLoading(true);
      setError(null);
      try {
        const data = await CategoryService.getTree(catalogueId);
        setTree(data);
      } catch (e) {
        setError(e instanceof Error ? e.message : "Failed to load categories");
      } finally {
        setLoading(false);
      }
    },
    [catalogueId]
  );

  useEffect(() => {
    loadTree();
  }, [loadTree]);

  const applyCategoryConfiguration = useCallback(
    (templateId: string) => CatalogueTemplateService.applyCategoryTemplate(catalogueId, templateId),
    [catalogueId]
  );

  const refreshAfterApply = useCallback(
    async () => {
      setSelectedId(GENERAL_ID);
      setEditorReload((n) => n + 1);
      setQueueNotice("Youzu is updating the affected products.");
      await loadTree();
    },
    [loadTree]
  );

  const noticeFromQueue = (queued: number) => {
    setQueueNotice(
      queued > 0
        ? `Youzu is updating ${queued} product${queued === 1 ? "" : "s"}.`
        : "Saved. No products needed updating.",
    );
  };

  const openCreate = (parent: CategoryTreeNode | null) => {
    setFormError(null);
    setForm({ mode: "create", parent });
  };

  const openRename = (node: CategoryTreeNode) => {
    setFormError(null);
    setForm({ mode: "rename", target: node });
  };

  const submitForm = async (values: CategoryFormValues) => {
    if (!form) return;
    setFormBusy(true);
    setFormError(null);
    try {
      if (form.mode === "create") {
        const created = await CategoryService.createCategory(catalogueId, {
          name: values.name,
          slug: values.slug,
          category_slug: values.category_slug,
          description: values.description,
          parentId: form.parent?.id ?? null,
        });
        noticeFromQueue(created.reprocessing?.queued ?? 0);
        await loadTree();
        setSelectedId(created.id);
      } else {
        const updated = await CategoryService.updateCategory(
          catalogueId,
          form.target.id,
          { name: values.name, description: values.description, category_slug: values.category_slug },
        );
        noticeFromQueue(updated.reprocessing?.queued ?? 0);
        await loadTree();
        setEditorReload((n) => n + 1);
      }
      setForm(null);
    } catch (e) {
      setFormError(e instanceof Error ? e.message : "Operation failed");
    } finally {
      setFormBusy(false);
    }
  };

  const confirmDelete = async (node: CategoryTreeNode) => {
    setConfirmBusy(true);
    setActionError(null);
    try {
      await CategoryService.deleteCategory(catalogueId, node.id);
      setQueueNotice("Youzu is re-filing the products that were in it.");
      if (selectedId === node.id) setSelectedId(GENERAL_ID);
      await loadTree();
      setConfirm(null);
    } catch (e) {
      setActionError(e instanceof Error ? e.message : "Delete failed");
      setConfirm(null);
    } finally {
      setConfirmBusy(false);
    }
  };

  const setEnabled = async (node: CategoryTreeNode, enabled: boolean) => {
    setConfirmBusy(true);
    setActionError(null);
    try {
      const updated = await CategoryService.updateCategory(catalogueId, node.id, {
        isCanonical: enabled,
      });
      noticeFromQueue(updated.reprocessing?.queued ?? 0);
      await loadTree();
      setConfirm(null);
    } catch (e) {
      setActionError(e instanceof Error ? e.message : "Update failed");
      setConfirm(null);
    } finally {
      setConfirmBusy(false);
    }
  };

  const toggleEnabled = (node: CategoryTreeNode) => {
    if (confirmBusy || statuses.get(node.id) === "Disabled by parent") return;
    if (node.isCanonical) {
      setConfirm({ kind: "disable", node });
      return;
    }
    void setEnabled(node, true);
  };

  return (
    <div className="space-y-4">
      {readOnly && (
        <div className="rounded-md border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800">
          This catalogue is deleted — category management is read-only.
        </div>
      )}
      {actionError && (
        <div className="rounded-md border border-destructive/30 bg-destructive/5 p-3 text-sm text-destructive">
          {actionError}
        </div>
      )}
      {queueNotice && (
        <div className="rounded-md border border-green-200 bg-green-50 p-3 text-sm text-green-800">
          {queueNotice}{" "}
          <Link
            className="font-medium underline"
            to={`/catalog/${catalogueId}?tab=products`}
          >
            View products
          </Link>
        </div>
      )}

      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <p className="max-w-[74ch] text-sm text-muted-foreground">
          <strong className="font-semibold text-foreground">
            Categories group your products.
          </strong>{" "}
          Pick one on the left to choose what Youzu reads from those products, and how
          it writes their titles and descriptions.
        </p>
        <Button
          type="button"
          variant="outline"
          onClick={() => setApplyConfigurationOpen(true)}
          disabled={readOnly}
        >
          Replace with a saved setup
        </Button>
      </div>

      {/* A definite height, not min-height: a min-height row still sizes to its
          content, which pushes the editor's pinned save bar off the page. */}
      <div className="flex items-stretch overflow-hidden rounded-lg border bg-card [height:clamp(560px,calc(100vh-17rem),900px)]">
        <CategoryNavigator
          nodes={tree}
          loading={loading}
          error={error}
          selectedId={selectedId === GENERAL_ID ? null : selectedId}
          draftIds={draftIds}
          readOnly={readOnly}
          pinnedEntry={{
            label: "Default attributes",
            hint: "Used by any category that has not been given attributes of its own",
            selected: selectedId === GENERAL_ID,
            onSelect: () => setSelectedId(GENERAL_ID),
          }}
          onSelect={(n) => setSelectedId(n.id)}
          onCreateRoot={() => openCreate(null)}
          renderRowMenu={(node) => (
            <CategoryRowMenu
              node={node}
              readOnly={readOnly || confirmBusy}
              disabledByParent={statuses.get(node.id) === "Disabled by parent"}
              onAdd={(parent) => openCreate(parent)}
              onRename={(target) => openRename(target)}
              onExpandBranch={() => undefined}
              onToggleEnabled={(target) => toggleEnabled(target)}
              onDelete={(target) => setConfirm({ kind: "delete", node: target })}
            />
          )}
        />

        {/* Editor panel */}
        <div className="flex min-h-0 min-w-0 flex-1 flex-col">
          <div className="shrink-0 border-b px-5 py-4">
            {selectedNode ? (
              <>
                <div className="mb-1.5 flex flex-wrap items-center gap-1.5 text-xs text-muted-foreground">
                  {ancestorsOf(tree, selectedNode.id).map((ancestor, i) => (
                    <span key={ancestor.id} className="flex items-center gap-1.5">
                      {i > 0 && <span className="text-muted-foreground/60">›</span>}
                      <button
                        type="button"
                        className="rounded hover:text-pink-700 hover:underline"
                        onClick={() => setSelectedId(ancestor.id)}
                      >
                        {localizedText(ancestor.name)}
                      </button>
                    </span>
                  ))}
                  {!ancestorsOf(tree, selectedNode.id).length && (
                    <span className="font-medium text-foreground">Top level</span>
                  )}
                </div>
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <h2 className="text-xl font-semibold">{localizedText(selectedNode.name)}</h2>
                  {!readOnly && (
                    <div className="flex items-center gap-2">
                      <div className="mr-1 flex items-center gap-1.5">
                        <Switch
                          checked={selectedStatus === "Enabled"}
                          disabled={confirmBusy || selectedStatus === "Disabled by parent"}
                          onCheckedChange={() => toggleEnabled(selectedNode)}
                          id="category-enabled"
                          aria-describedby={selectedStatus === "Disabled by parent" ? "category-disabled-parent" : undefined}
                        />
                        <Label htmlFor="category-enabled" className="text-xs text-muted-foreground">
                          {selectedStatus}
                        </Label>
                      </div>
                      {selectedStatus === "Disabled by parent" && (
                        <p id="category-disabled-parent" className="text-xs text-muted-foreground">
                          Enable the parent branch first.
                        </p>
                      )}
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => openCreate(selectedNode)}
                      >
                        <Plus className="mr-1 h-4 w-4" /> Subcategory
                      </Button>
                    </div>
                  )}
                </div>
              </>
            ) : (
              <>
                <p className="mb-1.5 text-xs font-medium text-muted-foreground">
                  Catalogue-wide fallback
                </p>
                <h2 className="text-xl font-semibold">Default attributes</h2>
                <p className="mt-1 max-w-[58ch] text-[13px] text-muted-foreground">
                  Used by any category that has not been given attributes of its own.
                  Categories with their own attributes ignore these.
                </p>
              </>
            )}
          </div>

          <SchemaEditor
            key={`${selectedId}-${editorReload}`}
            catalogueId={catalogueId}
            categoryId={selectedId === GENERAL_ID ? GENERAL_ID : (selectedId ?? GENERAL_ID)}
            isGeneral={selectedId === GENERAL_ID}
            parentName={
              selectedNode
                ? (localizedText(ancestorsOf(tree, selectedNode.id).slice(-1)[0]?.name ?? { en: "" }) || null)
                : null
            }
            languages={languages}
            readOnly={readOnly}
            drafts={draftStore}
            subcategoryIds={selectedNode ? descendantIds(selectedNode) : []}
            onDraftsChanged={() => setDraftIds(new Set(draftStore.ids()))}
            onSaved={() => {
              setDraftIds(new Set(draftStore.ids()));
              void loadTree();
            }}
          />
        </div>
      </div>

      <CategoryFormDialog
        open={!!form}
        mode={form?.mode ?? "create"}
        parent={form?.mode === "create" ? form.parent : null}
        target={form?.mode === "rename" ? form.target : null}
        nodes={tree}
        languages={languages}
        busy={formBusy}
        error={formError}
        onSubmit={submitForm}
        onOpenChange={(open) => !open && setForm(null)}
      />

      <ConfirmDialog
        open={confirm?.kind === "delete"}
        title={confirm?.kind === "delete" ? `Delete “${localizedText(confirm.node.name)}”?` : ""}
        body={
          confirm?.kind === "delete" ? (
            <>
              {descendantCount(confirm.node) > 0 && (
                <>
                  Its <strong>{descendantCount(confirm.node)}</strong>{" "}
                  {descendantCount(confirm.node) === 1 ? "subcategory" : "subcategories"}{" "}
                  will be deleted too.{" "}
                </>
              )}
              Everything set up on {descendantCount(confirm.node) > 0 ? "them" : "it"} is
              lost, and Youzu will re-file the products that were in{" "}
              {descendantCount(confirm.node) > 0 ? "them" : "it"}.
            </>
          ) : null
        }
        confirmLabel="Delete"
        destructive
        busy={confirmBusy}
        onConfirm={() => confirm?.kind === "delete" && confirmDelete(confirm.node)}
        onOpenChange={(open) => !open && setConfirm(null)}
      />

      <ConfirmDialog
        open={confirm?.kind === "disable"}
        title={
          confirm?.kind === "disable" ? `Disable “${localizedText(confirm.node.name)}”?` : ""
        }
        body={
          confirm?.kind === "disable" ? (
            <>
              Youzu will no longer select this category or any of its subcategories.
              Products assigned to this branch will be reclassified.
              {descendantCount(confirm.node) > 0 && (
                <>
                  {" "}
                  This affects <strong>{descendantCount(confirm.node)}</strong> subcategories.
                  Their individual settings are kept for when you enable this branch again.
                </>
              )}
            </>
          ) : null
        }
        confirmLabel="Disable"
        busy={confirmBusy}
        onConfirm={() =>
          confirm?.kind === "disable" && setEnabled(confirm.node, false)
        }
        onOpenChange={(open) => !open && setConfirm(null)}
      />

      <ApplyConfigurationModal
        open={applyConfigurationOpen}
        onOpenChange={setApplyConfigurationOpen}
        title="Replace with a saved setup"
        description="Replaces every category in this catalogue, and all their settings, with a setup saved earlier. Only published setups are listed."
        templateLabel="Category configuration"
        warning="Products stay where they are until Youzu re-files them."
        confirmLabel="I understand this replaces every category and all their settings."
        loadTemplates={CatalogueTemplateService.listPublishedCategoryTemplates}
        requiredLanguages={languages}
        applyTemplate={applyCategoryConfiguration}
        onApplied={refreshAfterApply}
      />
    </div>
  );
}
